import { motion, AnimatePresence } from 'framer-motion'
import type { TimelineEntry, ContentItem } from '../lib/types'

interface AttachmentsPanelProps {
  open: boolean
  entries: TimelineEntry[]
  onClose: () => void
  onScrollToEntry: (entryId: number) => void
}

/** Icon configs by content type */
function ContentTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'youtube':
      return (
        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      )
    case 'pdf':
      return (
        <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      )
    case 'notecard':
      return (
        <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
        </svg>
      )
    case 'text':
      return (
        <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      )
    default:
      // blog / generic web — globe icon
      return (
        <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A8.966 8.966 0 013 12c0-1.264.26-2.466.727-3.559" />
        </svg>
      )
  }
}

function getTitle(item: ContentItem): string {
  const meta = item.metadata as Record<string, string> | undefined
  if (meta?.title) return meta.title
  if (item.source_url) {
    try {
      const url = new URL(item.source_url)
      return url.hostname + url.pathname.slice(0, 40)
    } catch {
      return item.source_url
    }
  }
  if (item.file_path) {
    const parts = item.file_path.split('/')
    return parts[parts.length - 1] || 'Uploaded file'
  }
  return 'Content item'
}

function getThumbnail(item: ContentItem): string | null {
  const meta = item.metadata as Record<string, string> | undefined
  if (item.content_type === 'youtube') {
    if (meta?.thumbnail) return meta.thumbnail
    if (meta?.video_id) return `https://img.youtube.com/vi/${meta.video_id}/mqdefault.jpg`
  }
  return null
}

function hasExternalLink(item: ContentItem): boolean {
  return !!(item.source_url && (item.content_type === 'youtube' || item.content_type === 'blog'))
}

export default function AttachmentsPanel({
  open,
  entries,
  onClose,
  onScrollToEntry,
}: AttachmentsPanelProps) {
  const contentEntries = entries.filter((e) => e.content_item !== null)

  const handleItemTap = (entryId: number) => {
    onClose()
    // Small delay so the panel animates out before scrolling
    setTimeout(() => onScrollToEntry(entryId), 200)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />

          {/* Panel */}
          <motion.div
            className="relative bg-surface rounded-t-2xl max-h-[60vh] flex flex-col border-t border-elevated"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-text-muted/30" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <h3 className="text-sm font-semibold text-text-primary">
                Sources & Attachments
              </h3>
              <button
                onClick={onClose}
                className="text-text-secondary hover:text-text-primary p-1"
                aria-label="Close panel"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content list */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-6 space-y-2">
              {contentEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-text-muted">
                  <svg className="w-10 h-10 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                  <p className="text-sm">No imported content yet</p>
                </div>
              ) : (
                contentEntries.map((entry) => {
                  const item = entry.content_item!
                  const title = getTitle(item)
                  const thumbnail = getThumbnail(item)
                  const showExternalLink = hasExternalLink(item)

                  return (
                    <div
                      key={entry.id}
                      className="flex items-center gap-3 bg-elevated/50 hover:bg-elevated rounded-xl px-3 py-2.5 cursor-pointer transition-colors active:scale-[0.98]"
                      onClick={() => handleItemTap(entry.id)}
                    >
                      {/* Thumbnail or icon */}
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt=""
                          className="w-12 h-9 rounded-lg object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-surface flex items-center justify-center shrink-0">
                          <ContentTypeIcon type={item.content_type} />
                        </div>
                      )}

                      {/* Title */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {title}
                        </p>
                        <p className="text-xs text-text-muted capitalize">
                          {item.content_type === 'blog' ? 'Web article' : item.content_type}
                        </p>
                      </div>

                      {/* External link button */}
                      {showExternalLink && (
                        <a
                          href={item.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-text-muted hover:text-accent p-1.5 shrink-0 transition-colors"
                          aria-label="Open source"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
